import { FC, useEffect, useMemo, useState, useRef } from 'react'
import clsx from 'clsx'
import dynamic from 'next/dynamic'

import HTMLParseWrap from './html-parse-wrap/html-parse-wrap'
const WikiPopupWrapper = dynamic(() => import('./wiki-popup-wrapper/wiki-popup-wrapper'))
const RecomendationWebinarWrapper = dynamic(
  () => import('containers/recomendation-webinar-wrapper/recomendation-webinar-wrapper')
)

const BannerQuestionnaire = dynamic(
  () => import('components/banners/banner-questionnaire/banner-questionnaire')
)
const BannerQuestionnaireTeaser = dynamic(
  () => import('components/banners/banner-questionnaire-teaser/banner-questionnaire-teaser')
)
const RecommendationInner = dynamic(
  () =>
    import(
      'components/articles/article-material/ui/article-recomendation-inner/article-recomendation-inner'
    )
)
import PopupWrapper from 'containers/popup-wrapper/popup-wrapper'
import useMouseDragScroll from 'hooks/useMouseDragScroll'
import useOrientationChange from './useOrientationChange'

import { HtmlContentStatus } from 'constants/article-data'
import { CardsType } from 'constants/newsline'
import { Events } from 'constants/events'

import styles from './html-container.module.scss'
import { useAuth } from 'store/auth/useAuth'

interface IHtmlContainer {
  currentArticleId?: number
  htmlContent: string
  contentStatus?: string
  children?: React.ReactNode
  recomendation?: any
  noDivide?: boolean
  percentRatio?: number
}

type AnyEvent = MouseEvent | TouchEvent

const HtmlContainer: FC<IHtmlContainer> = ({
  currentArticleId,
  htmlContent,
  contentStatus,
  children,
  recomendation,
  noDivide = true,
  percentRatio,
}) => {
  const { isAuthorized } = useAuth()
  const scrollRef = useRef<HTMLDivElement>(null)
  const { onMouseDown } = useMouseDragScroll(scrollRef)
  const [wikiPopupText, setWikiPopupText] = useState('')
  const [imgPopupOpened, setImgPopupOpen] = useState('')

  const closePopup = () => setWikiPopupText('')
  const closeImgPopupOpen = () => setImgPopupOpen('')

  useOrientationChange(imgPopupOpened)
  useEffect(() => {
    const elements = document.querySelectorAll('.accordion_open')

    if (elements.length) {
      elements.forEach((el) => el.classList.remove('accordion_open'))
    }
    const handleClick = (e: AnyEvent) => {
      try {
        const currentNode = e.target as HTMLElement
        const parent = currentNode.parentNode as HTMLElement

        const isAccordion =
          currentNode.classList.contains('accordion-summary') &&
          parent.classList.contains('accordion')

        const isEditorWarning =
          currentNode.classList.contains('cdx-warning__title') &&
          parent.classList.contains('cdx-warning__content')

        if (isAccordion) {
          parent.classList.toggle('accordion_open')
        } else if (isEditorWarning) {
          parent.classList.toggle('cdx-warning__content_open')
        } else if (currentNode.classList.contains('ql-wiki') && currentNode.dataset.value) {
          setWikiPopupText(currentNode.dataset.value)
        } else if (currentNode.tagName === 'IMG' && !currentNode.dataset.image) {
          currentNode.classList.add('img_opened')
          setImgPopupOpen(currentNode.getAttribute('src') || '')
        }
      } catch (err) {
        console.log(err)
      }
    }

    scrollRef.current?.addEventListener(Events.CLICK, handleClick)
    return () => {
      scrollRef.current?.removeEventListener(Events.CLICK, handleClick)
    }
  }, [])

  const currentHTML = useMemo(() => {
    let Banner = <></>

    if (contentStatus === HtmlContentStatus.SHOW_REG_BANNER) {
      Banner = (
        <RecommendationInner recomendation={recomendation} currentArticleId={currentArticleId} />
      )
    } else if (contentStatus === HtmlContentStatus.SHOW_WEBINAR && recomendation.id) {
      Banner = (
        <div className={styles.bannerWrapper}>
          <RecomendationWebinarWrapper recommendWebinar={recomendation} />
        </div>
      )
    } else if (contentStatus === HtmlContentStatus.SHOW_QUESTIONNAIRE && recomendation) {
      if (recomendation.place === CardsType.FULL_MAIN)
        Banner = (
          <div className={styles.bannerWrapper}>
            <BannerQuestionnaire questionnaire={recomendation} />
          </div>
        )
      else if (recomendation.place === CardsType.TEASER_MAIN)
        Banner = (
          <div className={styles.bannerWrapper}>
            <BannerQuestionnaireTeaser {...recomendation} />
          </div>
        )
    } else if (recomendation && isAuthorized) {
      Banner = (
        <RecommendationInner recomendation={recomendation} currentArticleId={currentArticleId} />
      )
    }

    return Banner
  }, [contentStatus, recomendation])

  const isHorizontal = contentStatus === HtmlContentStatus.WITH_HORIZ_SCROLL

  return (
    <>
      <div
        className={clsx({
          [styles.hideContent]: contentStatus === HtmlContentStatus.HIDE_CONTENT,
        })}
      >
        <div onMouseDown={isHorizontal ? onMouseDown : undefined} ref={scrollRef}>
          <HTMLParseWrap htmlContent={htmlContent} noDivide={noDivide} percentRatio={percentRatio}>
            {currentHTML}
          </HTMLParseWrap>
        </div>

        {children}
      </div>
      <WikiPopupWrapper animationIn={Boolean(wikiPopupText)} handlePopup={closePopup}>
        {wikiPopupText}
      </WikiPopupWrapper>

      {imgPopupOpened && (
        <PopupWrapper
          popupBlockModificator={styles.imgPopup}
          showBtnClose={false}
          handlePopup={closeImgPopupOpen}
          isNoAuthPopup
        >
          <img
            src={imgPopupOpened}
            className={styles.imgInner}
            data-image="true"
            onClick={closeImgPopupOpen}
          />
        </PopupWrapper>
      )}
    </>
  )
}

export default HtmlContainer
